import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardTitle, Table, Td, Th, cn } from "@/core/ui";
import { useRealtimeOpen, useTopic } from "@/core/realtime/RealtimeProvider";
import { leaderboardTopic } from "@/core/realtime/protocol";
import { useLeaderboard, usePuzzleStats } from "./api";
import { formatDuration } from "./time";

export function LeaderboardPanel({ slug }: { slug: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const live = useRealtimeOpen();
  const board = useLeaderboard(slug, { live });
  const stats = usePuzzleStats(slug);
  // Rows are per-viewer (is_me), so the server only says "changed" and we refetch (ADR 0029).
  useTopic(leaderboardTopic(slug), (frame) => {
    if (frame.type !== "leaderboard.changed") return;
    void qc.invalidateQueries({ queryKey: ["leaderboard", slug] });
    void qc.invalidateQueries({ queryKey: ["stats", slug] });
  });
  const entries = board.data?.entries ?? [];
  // A Points column only where points order the board (ADR 0040).
  const scored = board.data?.scored ?? false;
  return (
    <Card className="bt-card--panel">
      <CardTitle>{t("nav.leaderboard")}</CardTitle>
      {stats.data && stats.data.started > 0 ? (
        <p className="bt-leaderboard__summary">
          {t("play.stats_line", {
            solved: stats.data.solved,
            started: stats.data.started,
            median: stats.data.median_ms !== null ? formatDuration(stats.data.median_ms) : "–",
          })}
        </p>
      ) : null}
      {entries.length === 0 ? (
        <p className="bt-leaderboard__empty">{t("play.leaderboard_empty")}</p>
      ) : null}
      {entries.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>{t("play.participant")}</Th>
              {scored ? <Th>{t("play.points")}</Th> : null}
              <Th>{t("play.time")}</Th>
              <Th>{t("play.date")}</Th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr
                key={`${e.rank}-${e.participant}`}
                className={cn(e.is_me && "bt-leaderboard__row--me")}
              >
                <Td>{e.rank}</Td>
                <Td>
                  {e.participant}
                  {e.is_team ? (
                    <span className="bt-leaderboard__team-tag">({t("play.team")})</span>
                  ) : null}
                </Td>
                {scored ? <Td className="bt-table__cell--numeric">{e.score ?? "–"}</Td> : null}
                <Td className="bt-table__cell--numeric bt-table__cell--duration">
                  {formatDuration(e.elapsed_ms)}
                </Td>
                <Td>{new Date(e.submitted_at).toLocaleDateString(i18n.language)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </Card>
  );
}
